import React, { useEffect, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useDispatch, useSelector } from "react-redux"
import {
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
} from "reactstrap"
import GeneralTables from "components/Common/GeneralTable"
import CSAlert from "components/Alert/ConfirmationAlert"
import {
  getAdminProviderTransfers,
  approveProviderTransfers,
  rejectProviderTransfers,
} from "store/provider-transfer/provider-transfer-action"

const AdminProviderTransfers = () => {
  document.title = "Provider Transfer Management"

  const dispatch = useDispatch()
  const { data, loading } = useSelector(state => state.providerTransfer)
  const [pageNumber, setPageNumber] = useState(10)
  const [selectedIds, setSelectedIds] = useState([])
  const [statusFilter, setStatusFilter] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [modalAction, setModalAction] = useState(null)
  const [adminNote, setAdminNote] = useState("")
  const [targetIds, setTargetIds] = useState([])

  useEffect(() => {
    dispatch(getAdminProviderTransfers(pageNumber, statusFilter))
  }, [pageNumber, statusFilter])

  const onPageClick = link => {
    dispatch(getAdminProviderTransfers(link.url, pageNumber, statusFilter))
  }

  const onChangeRowNumber = value => {
    setPageNumber(value)
  }

  const onCheckboxClick = (e, id) => {
    const check = selectedIds.includes(id)
    if (check) {
      setSelectedIds(selectedIds.filter(myId => myId !== id))
    } else {
      setSelectedIds([...selectedIds, id])
    }
  }

  const onParentCheckChange = status => {
    if (status) {
      const pending =
        data?.data
          ?.filter(item => item.status === "Pending")
          .map(item => item.id) || []
      setSelectedIds(pending)
    } else {
      setSelectedIds([])
    }
  }

  const openApprovalModal = (action, ids) => {
    setModalAction(action)
    setTargetIds(ids)
    setAdminNote("")
    setShowModal(true)
  }

  const handleConfirm = () => {
    setShowModal(false)
    if (modalAction === "approve") {
      dispatch(
        approveProviderTransfers(targetIds, adminNote || null, success => {
          if (success) setSelectedIds([])
        })
      )
    } else if (modalAction === "reject") {
      dispatch(
        rejectProviderTransfers(targetIds, adminNote || null, success => {
          if (success) setSelectedIds([])
        })
      )
    }
  }

  const handleApprove = () => {
    if (selectedIds.length === 0) return
    openApprovalModal("approve", selectedIds)
  }

  const handleReject = () => {
    if (selectedIds.length === 0) return
    openApprovalModal("reject", selectedIds)
  }

  const statusBadge = status => {
    switch (status) {
      case "Pending":
        return <Badge color="warning">Pending</Badge>
      case "Approved":
        return <Badge color="success">Approved</Badge>
      case "Rejected":
        return <Badge color="danger">Rejected</Badge>
      default:
        return <Badge color="secondary">{status}</Badge>
    }
  }

  return (
    <div className="page-content">
      <Breadcrumbs title="Admin" breadcrumbItem="Provider Transfer Requests" />
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <Button
                color="success"
                size="sm"
                className="me-2"
                onClick={handleApprove}
                disabled={selectedIds.length === 0 || loading}
              >
                <i className="mdi mdi-check me-1" />
                Approve Selected
              </Button>
              <Button
                color="danger"
                size="sm"
                onClick={handleReject}
                disabled={selectedIds.length === 0 || loading}
              >
                <i className="mdi mdi-close me-1" />
                Reject Selected
              </Button>
            </div>
            <div className="d-flex gap-2">
              {["Pending", "Approved", "Rejected"].map(status => (
                <Button
                  key={status}
                  size="sm"
                  color={
                    statusFilter === status ? "primary" : "outline-secondary"
                  }
                  onClick={() =>
                    setStatusFilter(statusFilter === status ? null : status)
                  }
                >
                  {status}
                </Button>
              ))}
            </div>
          </div>

          <GeneralTables
            data={data?.data}
            loading={loading}
            parentCheckbox={true}
            onParentCheckChange={onParentCheckChange}
            headers={[
              "SN",
              "Item",
              "Batch",
              "Transfer Qty",
              "From Provider",
              "Status",
              "Requested At",
              "Action",
            ]}
            pagination={{ meta: data?.meta, onPageClick: onPageClick }}
            renderItem={(transfer, index) => (
              <tr key={transfer.id}>
                <th scope="row">
                  <div className="form-check form-checkbox-outline form-check-primary mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`checkbox-${transfer.id}`}
                      checked={selectedIds.includes(transfer.id)}
                      onChange={e => onCheckboxClick(e, transfer.id)}
                      disabled={transfer.status !== "Pending"}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`checkbox-${transfer.id}`}
                    >
                      {data?.meta?.from + index}
                    </label>
                  </div>
                </th>
                <td>
                  {transfer?.provider_in?.item?.ip_item_description ||
                    transfer?.provider_in?.item?.unfp_item_name ||
                    "-"}
                </td>
                <td>{transfer?.provider_in?.batch_number || "-"}</td>
                <td>
                  Total: {transfer.total_receipt_qty} | Used:{" "}
                  {transfer.used_qty} | Available: {transfer.available_qty} |{" "}
                  <strong>Transfer: {transfer.transfer_qty}</strong>
                </td>
                <td>{transfer?.requested_by?.name || "-"}</td>
                <td>{statusBadge(transfer.status)}</td>
                <td>{new Date(transfer.created_at).toLocaleDateString()}</td>
                <td>
                  {transfer.status === "Pending" && (
                    <div className="d-flex gap-2">
                      <Button
                        color="success"
                        size="sm"
                        onClick={() =>
                          openApprovalModal("approve", [transfer.id])
                        }
                        disabled={loading}
                      >
                        {loading ? (
                          <Spinner size="sm" />
                        ) : (
                          <i className="mdi mdi-check" />
                        )}
                      </Button>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() =>
                          openApprovalModal("reject", [transfer.id])
                        }
                        disabled={loading}
                      >
                        {loading ? (
                          <Spinner size="sm" />
                        ) : (
                          <i className="mdi mdi-close" />
                        )}
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            )}
          />
        </CardBody>
      </Card>

      <Modal isOpen={showModal} toggle={() => setShowModal(false)} centered>
        <ModalHeader toggle={() => setShowModal(false)}>
          {modalAction === "approve" ? "Approve" : "Reject"} Transfer Request
          {targetIds.length > 1 ? "s" : ""}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="adminNote">
              Admin Note <span className="text-muted">(optional)</span>
            </Label>
            <Input
              type="textarea"
              id="adminNote"
              rows={3}
              value={adminNote}
              onChange={e => setAdminNote(e.target.value)}
              placeholder="Enter a note for this action..."
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            color={modalAction === "approve" ? "success" : "danger"}
            onClick={handleConfirm}
          >
            Proceed
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default AdminProviderTransfers
